import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from '@evertel/di'
import { Api } from '@evertel/api'
import { APIDataDocumentSchema, APIDataDocumentSchemaCategory } from '@evertel/types'
import { DocumentSchemaStore } from '../../store'


class GlobalSchemaCategoriesController {
    categories: APIDataDocumentSchemaCategory[] = []

    constructor(
        private api: Api,
        private documentSchemaStore: DocumentSchemaStore
    ) {
        makeAutoObservable(this)
    }

    // withSchemas: opt-in for screens that display each category's templates (e.g. the
    // Evertel Catalog), so they come back in the same request instead of one per category
    init = async (withSchemas = false) => {
        await this.fetchSchemaCategories(withSchemas)
    }

    fetchSchemaCategories = async (withSchemas = false) => {
        const categories = await this.api.Routes.DocumentSchemaCategory.get({
            where: {
                departmentId: 0
            },
            // array form is required: loopback-connector's buildOrderBy silently drops
            // everything after the first key when given a comma-separated string
            order: ['ordinal ASC', 'name ASC'],
            ...(withSchemas && {
                include: [{
                    relation: 'documentSchemas',
                    scope: {
                        include: ['iconMedia'],
                        order: 'name ASC'
                    }
                }]
            })
        }) as APIDataDocumentSchemaCategory[]

        // keep the store current so preview/clone/edit flows see the same schema objects
        const schemas = categories.flatMap(category => category.documentSchemas ?? [])
        if (schemas.length) this.documentSchemaStore.update(schemas)

        // update observables
        runInAction(() => {
            this.categories = categories
        })
    }

    schemasForCategory = (categoryId: number): APIDataDocumentSchema[] =>
        this.categories.find(category => category.id === categoryId)?.documentSchemas ?? []

    // flattened, de-duped view across every category (a schema can belong to more than one)
    get allSchemas(): APIDataDocumentSchema[] {
        const byId = new Map<number, APIDataDocumentSchema>()
        this.categories.forEach(category =>
            category.documentSchemas?.forEach(schema => byId.set(schema.id as number, schema))
        )
        return Array.from(byId.values())
    }
}

decorate(injectable(), GlobalSchemaCategoriesController)
decorate(inject(Api), GlobalSchemaCategoriesController, 0)
decorate(inject(DocumentSchemaStore), GlobalSchemaCategoriesController, 1)

export { GlobalSchemaCategoriesController }
